import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const SERVICES = [
    { key: "privat_haushalt", label: "services.privat.title" },
    { key: "scheiben", label: "services.scheiben.title" },
    { key: "begleitservice", label: "services.begleit.title" },
    { key: "buero", label: "services.buero.title" },
    { key: "gebaeude", label: "services.gebaeude.title" },
];

// Conditional field schema per service type
const DETAIL_FIELDS = {
    privat_haushalt: [
        { key: "rooms", label: "Anzahl Zimmer", type: "number", placeholder: "z.B. 3.5", min: 1, step: 0.5 },
        { key: "sqm", label: "Wohnfläche (m²)", type: "number", placeholder: "z.B. 85" },
        { key: "bathrooms", label: "Anzahl Bäder/WC", type: "number", placeholder: "z.B. 1", min: 0 },
        { key: "frequency", label: "Häufigkeit", type: "select", options: [
            { v: "einmalig", l: "Einmalig" },
            { v: "woechentlich", l: "Wöchentlich" },
            { v: "zwei_wochen", l: "Alle 2 Wochen" },
            { v: "monatlich", l: "Monatlich" },
            { v: "endreinigung", l: "Endreinigung (Umzug)" },
        ]},
        { key: "extras", label: "Zusatzoptionen (mehrfach)", type: "multi", options: [
            "Kühlschrank innen",
            "Backofen",
            "Balkon",
            "Fensterreinigung",
            "Stark verschmutzt",
        ]},
    ],
    scheiben: [
        { key: "window_count", label: "Anzahl Fenster", type: "number", placeholder: "z.B. 12" },
        { key: "frames", label: "Rahmen mitreinigen?", type: "select", options: [
            { v: "ja", l: "Ja, mit Rahmen" },
            { v: "nein", l: "Nein, nur Glas" },
        ]},
        { key: "sides", label: "Seiten", type: "select", options: [
            { v: "innen", l: "Nur Innen" },
            { v: "aussen", l: "Nur Aussen" },
            { v: "beide", l: "Innen & Aussen" },
        ]},
        { key: "object_type", label: "Objekt", type: "select", options: [
            { v: "wohnung", l: "Wohnung / Haus" },
            { v: "wintergarten", l: "Wintergarten" },
            { v: "schaufenster", l: "Schaufenster / Glasfront" },
            { v: "buero", l: "Büro" },
        ]},
    ],
    begleitservice: [
        { key: "hours", label: "Gewünschte Stunden", type: "number", placeholder: "z.B. 3", min: 2, step: 0.5 },
        { key: "service_kind", label: "Art der Hilfe", type: "select", options: [
            { v: "einkauf", l: "Einkaufshilfe" },
            { v: "termin", l: "Begleitung zu Terminen" },
            { v: "alltag", l: "Alltagshilfe zuhause" },
            { v: "gesellschaft", l: "Gesellschaft / Besuch" },
            { v: "kombiniert", l: "Kombination" },
        ]},
        { key: "frequency", label: "Häufigkeit", type: "select", options: [
            { v: "einmalig", l: "Einmalig" },
            { v: "woechentlich", l: "Wöchentlich" },
            { v: "monatlich", l: "Monatlich" },
        ]},
    ],
    buero: [
        { key: "sqm", label: "Fläche (m²)", type: "number", placeholder: "z.B. 150" },
        { key: "rooms", label: "Anzahl Räume", type: "number", placeholder: "z.B. 5", min: 1 },
        { key: "frequency", label: "Häufigkeit", type: "select", options: [
            { v: "einmalig", l: "Einmalig" },
            { v: "woechentlich", l: "Wöchentlich" },
            { v: "zwei_wochen", l: "Alle 2 Wochen" },
            { v: "taeglich", l: "Täglich" },
        ]},
        { key: "extras", label: "Zusatzoptionen (mehrfach)", type: "multi", options: [
            "Fensterreinigung",
            "Glastrennwände",
            "Eingangsbereich",
            "Sanitäranlagen",
        ]},
    ],
    gebaeude: [
        { key: "sqm", label: "Fläche (m²)", type: "number", placeholder: "z.B. 400" },
        { key: "floors", label: "Anzahl Stockwerke", type: "number", placeholder: "z.B. 3", min: 1 },
        { key: "object_type", label: "Objekt", type: "select", options: [
            { v: "fassade", l: "Fassade" },
            { v: "glasfront", l: "Glasfront" },
            { v: "treppenhaus", l: "Treppenhaus" },
            { v: "mehrere", l: "Mehrere Bereiche" },
        ]},
    ],
};

export default function BookingForm() {
    const { t } = useLang();
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        address: "",
        city: "",
        postal_code: "",
        service_type: "",
        preferred_date: "",
        preferred_time: "",
        message: "",
    });
    const [details, setDetails] = useState({});

    // Reset details when service changes
    useEffect(() => { setDetails({}); }, [form.service_type]);

    const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
    const updateDetail = (k, v) => setDetails((p) => ({ ...p, [k]: v }));
    const toggleMulti = (k, opt) => {
        const arr = Array.isArray(details[k]) ? details[k] : [];
        const next = arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt];
        updateDetail(k, next);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { ...form, service_details: details };
            if (!payload.customer_email) delete payload.customer_email;
            await api.post("/bookings/public", payload);
            setSubmitted(true);
            toast.success(t("booking.success"));
            setForm({ customer_name: "", customer_email: "", customer_phone: "", address: "", city: "", postal_code: "", service_type: "", preferred_date: "", preferred_time: "", message: "" });
            setDetails({});
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="rounded-3xl bg-[#F0FDF4] border border-[#16A34A]/20 p-12 text-center" data-testid="booking-success-card">
                <div className="inline-flex w-16 h-16 rounded-full bg-[#16A34A] text-white items-center justify-center mb-5 mx-auto">
                    <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-display font-bold text-2xl text-[#1A365D] mb-2">{t("booking.success")}</h3>
                <p className="text-[#64748B] mb-6">Wir melden uns innerhalb von 24 Stunden mit einer Richtofferte.</p>
                <button onClick={() => setSubmitted(false)} className="btn-secondary" data-testid="booking-new-button">Neue Anfrage</button>
            </div>
        );
    }

    const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/15 transition";
    const labelCls = "block text-xs font-semibold uppercase tracking-wider text-[#1A365D]/70 mb-1.5";

    const fields = form.service_type ? DETAIL_FIELDS[form.service_type] || [] : [];

    return (
        <form onSubmit={onSubmit} className="rounded-3xl bg-white border border-slate-200 p-8 md:p-10 shadow-xl shadow-slate-200/50" data-testid="booking-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className={labelCls}>{t("booking.name")} *</label>
                    <input required value={form.customer_name} onChange={update("customer_name")} className={inputCls} data-testid="booking-name-input" />
                </div>
                <div>
                    <label className={labelCls}>{t("booking.phone")} *</label>
                    <input required value={form.customer_phone} onChange={update("customer_phone")} className={inputCls} data-testid="booking-phone-input" />
                </div>
                <div className="md:col-span-2">
                    <label className={labelCls}>{t("booking.email")}</label>
                    <input type="email" value={form.customer_email} onChange={update("customer_email")} className={inputCls} data-testid="booking-email-input" />
                </div>
                <div className="md:col-span-2">
                    <label className={labelCls}>{t("booking.address")} *</label>
                    <input required value={form.address} onChange={update("address")} className={inputCls} data-testid="booking-address-input" />
                </div>
                <div>
                    <label className={labelCls}>{t("booking.postal")}</label>
                    <input value={form.postal_code} onChange={update("postal_code")} className={inputCls} data-testid="booking-postal-input" />
                </div>
                <div>
                    <label className={labelCls}>{t("booking.city")}</label>
                    <input value={form.city} onChange={update("city")} className={inputCls} data-testid="booking-city-input" />
                </div>
                <div className="md:col-span-2">
                    <label className={labelCls}>{t("booking.service")} *</label>
                    <select required value={form.service_type} onChange={update("service_type")} className={inputCls} data-testid="booking-service-select">
                        <option value="">{t("booking.select_service")}</option>
                        {SERVICES.map((s) => <option key={s.key} value={s.key}>{t(s.label)}</option>)}
                    </select>
                </div>
            </div>

            {/* Dynamic service-specific details */}
            {fields.length > 0 && (
                <div className="mt-6 rounded-2xl border border-[#16A34A]/20 bg-[#F0FDF4]/50 p-5 md:p-6" data-testid="service-details-section">
                    <div className="text-xs font-bold uppercase tracking-wider text-[#16A34A] mb-4">Details für eine genaue Offerte</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {fields.map((f) => (
                            <div key={f.key} className={f.type === "multi" ? "md:col-span-2" : ""}>
                                <label className={labelCls}>{f.label}</label>
                                {f.type === "number" && (
                                    <input
                                        type="number"
                                        min={f.min}
                                        step={f.step}
                                        placeholder={f.placeholder}
                                        value={details[f.key] ?? ""}
                                        onChange={(e) => updateDetail(f.key, e.target.value)}
                                        className={inputCls}
                                        data-testid={`detail-${f.key}-input`}
                                    />
                                )}
                                {f.type === "select" && (
                                    <select value={details[f.key] ?? ""} onChange={(e) => updateDetail(f.key, e.target.value)} className={inputCls} data-testid={`detail-${f.key}-select`}>
                                        <option value="">Bitte wählen…</option>
                                        {f.options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                                    </select>
                                )}
                                {f.type === "multi" && (
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        {f.options.map((opt) => {
                                            const active = (details[f.key] || []).includes(opt);
                                            return (
                                                <button
                                                    type="button"
                                                    key={opt}
                                                    onClick={() => toggleMulti(f.key, opt)}
                                                    data-testid={`detail-${f.key}-${opt}`}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[#16A34A] text-white border-[#16A34A]" : "bg-white text-[#1A365D] border-slate-200 hover:border-[#16A34A]"}`}
                                                >
                                                    {opt}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                <div>
                    <label className={labelCls}>{t("booking.date")} *</label>
                    <input required type="date" value={form.preferred_date} onChange={update("preferred_date")} className={inputCls} data-testid="booking-date-input" />
                </div>
                <div>
                    <label className={labelCls}>{t("booking.time")}</label>
                    <input type="time" value={form.preferred_time} onChange={update("preferred_time")} className={inputCls} data-testid="booking-time-input" />
                </div>
                <div className="md:col-span-2">
                    <label className={labelCls}>{t("booking.message")}</label>
                    <textarea rows={3} value={form.message} onChange={update("message")} className={inputCls} data-testid="booking-message-input" />
                </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary mt-8 w-full md:w-auto" data-testid="booking-submit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {t("booking.submit")}
            </button>
        </form>
    );
}
